import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {assert} from 'Asserts';
import Button, {ButtonProps, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, GRAY_LIGHT} from 'ui-library/Colors';

export interface LegacyToggleGroupOption<T> {
  id: T;
  buttonProps: Pick<ButtonProps, 'children' | 'text' | 'icon' | 'iconAltText' | 'id'>;
}

export interface LegacyToggleGroupProps<T> {
  disabled?: boolean;
  leftOption: LegacyToggleGroupOption<T>;
  rightOption: LegacyToggleGroupOption<T>;
  selection: T;
  onChange: (selection: T) => void;
  size: ButtonSize;
}

/**
 * A selector that allows the user to choose one of two options.
 *
 * @deprecated It is recommended that clients use the ToggleGroup component instead.
 */
export default function LegacyToggleGroup<T extends React.Key>({
  disabled,
  leftOption,
  rightOption,
  selection,
  onChange,
  size,
}: LegacyToggleGroupProps<T>): JSX.Element {
  const options = [leftOption, rightOption];
  assert(
    options.some((option) => option.id === selection),
    'Invalid selection was not on of the two valid options'
  );
  return (
    <div className={css(LegacyToggleGroupStyles.wrapper)}>
      {options.map((option) => (
        <Button
          className={classNames(
            option.id === selection
              ? css(LegacyToggleGroupStyles.selected)
              : css(LegacyToggleGroupStyles.deselected),
            disabled ? css(LegacyToggleGroupStyles.disabled) : ''
          )}
          key={option.id}
          disabled={disabled}
          {...option.buttonProps}
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          fullWidth={true}
          onClick={() => {
            onChange(option.id);
          }}
          size={size}
        />
      ))}
    </div>
  );
}

export const LegacyToggleGroupStyles = StyleSheet.create({
  selected: {
    backgroundColor: GRAY,
    ':focus': {
      'webkit-box-shadow': 'none',
      boxShadow: 'none',
    },
  },
  deselected: {
    backgroundColor: GRAY_LIGHT,
  },
  disabled: {
    pointerEvents: 'none',
  },
  wrapper: {
    display: 'flex',
    width: '100%',
    borderRadius: 4,
    padding: 4,
    backgroundColor: GRAY_LIGHT,
  },
});
